import { isSeatPackage } from './tourScoring';
import { isValidTourName } from './tourValidation';
import { Tour } from '@/types';

/**
 * Normalizes a tour name to its core identity by stripping:
 * - Ticket package prefixes (e.g., "Venue Premium - Tour" → "Tour")
 * - Artist name prefix patterns (e.g., "RAYE - THIS" → "THIS")
 * - "with [Guest Name]" suffixes
 * - Featured artist indicators
 * - Package suffixes (e.g., "Tour | VIP Package" → "Tour")
 * - Leading special characters
 * - Age restriction labels
 * - Trailing year tokens (e.g., "Tour 2026" → "Tour")
 * - Venue suffixes (e.g., "Tour @ Rialto Theatre" → "Tour")
 */
export const normalizeTourName = (name: string, artistName?: string): string => {
  let normalized = name.trim();
  
  // Remove everything before and including leading pipe (package variants)
  if (/^\|/.test(normalized)) {
    // This is a package variant, strip the pipe and leading content
    normalized = normalized.replace(/^.*\|\s*/, '');
  }
  
  // Remove leading asterisks, dashes, colons, commas, pipes, quotes, apostrophes
  normalized = normalized.replace(/^[\*\-:,|\s'"'`]+/, '');
  
  // Strip radio station prefixes (e.g., "92.5 XTU Anniversary Show -")
  normalized = normalized.replace(/^\d+\.\d+\s*[A-Z]{2,4}\s+.*?[-:]\s*/i, '');
  
  // Strip venue name prefixes (e.g., "Plaza de Parking-", "The Orangery-")
  normalized = normalized.replace(/^(plaza\s*de\s*\w+|the\s*orangery|fortnum\s*&?\s*mason|function\s*room)\s*[-:]\s*/i, '');
  
  // Strip ticket package prefixes (before artist name stripping)
  normalized = normalized.replace(/^(venue\s*premium|club\s*level(\s*seating)?|mezzanine|balcony|platinum|vip|gold|silver)\s*[-:]\s*/i, '');
  
  // Strip artist name prefix patterns like "Artist - Tour" or "Artist: Tour" or "*Artist - Tour"
  if (artistName && artistName.trim()) {
    const escapedArtist = artistName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Match patterns: "Artist - Tour", "Artist: Tour", "Artist – Tour" (em-dash)
    const artistPrefixPattern = new RegExp(`^\\*?${escapedArtist}\\s*[-–:]+\\s*`, 'i');
    normalized = normalized.replace(artistPrefixPattern, '');
  }
  
  // Strip "with [Guest]", "w/ [Guest]", "+ [Guest]", "feat. [Guest]" suffixes
  normalized = normalized.replace(/\s*(with|w\/|\+|feat\.?|featuring)\s+.*/i, '');
  
  // Strip venue suffixes: "@ Venue Name" or "at Venue Name" patterns
  // This handles: "Tour @ Rialto Theatre", "Tour at Madison Square Garden"
  normalized = normalized.replace(/\s*[@]\s*[A-Z][a-zA-Z\s&'-]+$/i, '');
  normalized = normalized.replace(/\s+at\s+[A-Z][a-zA-Z\s&'-]+$/i, '');
  
  // Strip trailing year tokens (e.g., "Tour 2026", "Tour '26")
  // Only strip if it looks like a branding year suffix, not part of the tour name
  normalized = normalized.replace(/\s+['']?\d{2}$/, ''); // '26 or 26
  normalized = normalized.replace(/\s+20\d{2}$/, ''); // 2024, 2025, 2026, etc.
  
  // Strip city-specific suffixes (e.g., "Tour - Rio de Janeiro", "Tour - São Paulo")
  // Match: " - [City Name]" where city is 1-3 words starting with capital letters
  normalized = normalized.replace(/\s*[-–]\s*[A-Z][a-zéáíóúñ]*(?:\s+(?:de\s+)?[A-Z][a-zéáíóúñ]*){0,2}$/i, '');
  
  // Strip "Part II", "Part 2", "Pt. 2" suffixes
  normalized = normalized.replace(/\s+(part|pt\.?)\s*(ii|iii|iv|2|3|4)$/i, '');
  
  // Strip package suffixes (pipe or dash separated) - expanded patterns
  normalized = normalized.replace(/\s*[-|]\s*(vip|platinum|gold|silver|premium|meet\s*&\s*greet|vinyl\s*room|club\s*level|package|experience|packages|all|box\s*seat|comfort\s*seat|business\s*seat|upgrade|loop|comfort\s*seats?).*$/i, '');
  
  // Strip trailing "& [Guest]" but preserve core band names with &
  normalized = normalized.replace(/\s*&\s+(?!the\s)[A-Z][a-zA-Z\s]*$/i, '');
  
  // Remove age restriction suffixes
  normalized = normalized.replace(/\s*\(?\d{2}\+\s*(event|show|only)?\)?$/i, '');
  
  // Remove trailing guest list patterns (e.g., ", Seph Schlueter")
  normalized = normalized.replace(/,\s+[A-Z][a-zA-Z\s]+$/i, '');
  
  // Remove trailing "+ Artist" patterns  
  normalized = normalized.replace(/\s*\+\s+[A-Z][a-zA-Z\s]+$/i, '');
  
  // Remove trailing punctuation
  normalized = normalized.replace(/[\s\-:,|]+$/, '');
  
  // Strip leading "THE " for deduplication (common variant)
  normalized = normalized.replace(/^THE\s+/i, '');
  
  return normalized.trim();
};

/**
 * Creates a deduplication key from tour name + artist + year
 * Key format: "{artistId}-{year}-{normalizedName}"
 */
export const getTourDedupeKey = (tour: Tour, artistName?: string): string => {
  const normalizedName = normalizeTourName(tour.name, artistName)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  return `${tour.artistId || 'unknown'}-${tour.year || 'unknown'}-${normalizedName}`;
};

/**
 * Extended Tour type with optional variant tracking
 */
export type DeduplicatedTour = Tour & {
  _variantCount?: number;
  _variants?: string[];
};

/**
 * Deduplicates an array of tours by normalized name + artist + year.
 * Filters out seat packages, keeps tour with highest activity (concerts + reviews).
 * Optionally tracks how many variants were consolidated.
 * 
 * @param tours - Array of tours to deduplicate
 * @param trackVariants - Whether to track consolidated variant info (default: false)
 * @returns Deduplicated array of tours
 */
export const deduplicateTours = (
  tours: Tour[],
  trackVariants = false
): DeduplicatedTour[] => {
  // Phase 1: Filter out seat packages and invalid tour names entirely
  const validTours = tours.filter(tour => {
    // Check seat package first (faster)
    if (isSeatPackage(tour.name)) return false;
    
    // Then check comprehensive validation
    const validation = isValidTourName(tour.name);
    return validation.valid;
  });
  
  const tourMap = new Map<string, DeduplicatedTour>();
  
  for (const tour of validTours) {
    const artistName = tour.artistName || '';
    const key = getTourDedupeKey(tour, artistName);
    const existing = tourMap.get(key);
    
    if (!existing) {
      const deduped: DeduplicatedTour = { ...tour };
      if (trackVariants) {
        deduped._variantCount = 1;
        deduped._variants = [tour.name];
      }
      tourMap.set(key, deduped);
    } else {
      // Track variant info if enabled
      if (trackVariants) {
        existing._variantCount = (existing._variantCount || 1) + 1;
        existing._variants = existing._variants || [existing.name];
        if (!existing._variants.includes(tour.name)) {
          existing._variants.push(tour.name);
        }
      }
      
      // Keep the tour with higher concert count, then higher review count
      const existingScore = (existing.concertCount || 0) * 1000 + (existing.reviewCount || 0);
      const newScore = (tour.concertCount || 0) * 1000 + (tour.reviewCount || 0);
      
      if (newScore > existingScore) {
        // Preserve variant tracking from existing entry
        const variantCount = existing._variantCount;
        const variants = existing._variants;
        const replacement: DeduplicatedTour = { ...tour };
        if (trackVariants) {
          replacement._variantCount = variantCount;
          replacement._variants = variants;
        }
        tourMap.set(key, replacement);
      }
    }
  }
  
  return Array.from(tourMap.values());
};
